import { useCallback, useRef } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import {
  idDocumentSchema,
  type IdDocumentFormValues,
} from '~domains/profile/entities/person';

import { ampli } from '~entities/ampli';
import {
  isOcrSuccessResponse,
  useConfirmOcrResult,
  useGetOcrResult,
  useUploadOcrImage,
} from '~entities/file';
import { FEATURE_FLAGS } from '~entities/growthbook';

import { FileUpload, type FileUploadRef } from '~shared/ui/file-upload/rhf';
import { FooterWithProgressBar } from '~shared/ui/footer-with-progress-bar';
import { Screen } from '~shared/ui/screen';

import { Select } from '@breezeventures/ui-kit/select/rhf';
import { toast } from '@breezeventures/ui-kit/toast';
import { Typography } from '@breezeventures/ui-kit/typography';
import { InputMasked } from '@breezeventures/web-ph-components/input-masked';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { zodResolver } from '@hookform/resolvers/zod';

import { PersonFormHeader } from '../../../person-form-header';
import {
  documentMetaMap,
  idDocumentTypeNames,
  idDocumentTypeOptions,
  idDocumentUploadLabels,
} from '../lib';

type IdDocumentProps = {
  initialValues?: Partial<IdDocumentFormValues>;
  onBack: () => void;
  onSubmit: (values: IdDocumentFormValues) => void;
};

export const IdDocument = ({
  initialValues,
  onBack,
  onSubmit,
}: IdDocumentProps) => {
  const fileUploadRef = useRef<FileUploadRef>(null);
  const methods = useForm<IdDocumentFormValues>({
    resolver: zodResolver(idDocumentSchema),
    mode: 'all',
    defaultValues: initialValues ?? {
      type: undefined,
      idNumber: '',
      document: undefined,
    },
  });

  const selectedType = methods.watch('type');
  const hasUploadedDocument = methods.watch('document');

  const isMySssOn = useFeatureIsOn(FEATURE_FLAGS.MY_SSS);
  const typeOptions = isMySssOn
    ? idDocumentTypeOptions
    : idDocumentTypeOptions.filter((option) => option.value !== 'MY_SSS');

  const { mutateAsync: uploadOcrImage } = useUploadOcrImage();
  const { mutateAsync: getOcrResult } = useGetOcrResult();
  const { mutateAsync: confirmOcrResult } = useConfirmOcrResult();

  const handleUpload = useCallback(
    async (
      request: { document: File },
      options?: { signal?: AbortSignal }
    ): Promise<{ documentId: string; fileName?: string }> => {
      const { transactionId } = await uploadOcrImage({
        fileItem: {
          frontImage: request.document,
          docType: selectedType,
        },
        signal: options?.signal,
      });

      const ocrRes = await getOcrResult({
        transactionId,
        signal: options?.signal,
      });
      if (isOcrSuccessResponse(ocrRes) && ocrRes.OCRresult?.idNumber) {
        methods.setValue('idNumber', ocrRes.OCRresult.idNumber, {
          shouldValidate: true,
        });
      }

      ampli.mpProfileIdDocumentUploaded();
      return {
        documentId: transactionId,
        fileName: request.document.name,
      };
    },
    [uploadOcrImage, selectedType, getOcrResult, methods]
  );

  const handleSubmit = async (values: IdDocumentFormValues) => {
    if (fileUploadRef.current?.checkIsUploading()) {
      toast('Still uploading, boss!', { type: 'info' });
      return;
    }

    await confirmOcrResult({
      confirmedResult: { idNumber: values.idNumber },
      transactionId: values.document.documentId,
    });

    onSubmit(values);
  };

  return (
    <form
      onSubmit={methods.handleSubmit(handleSubmit)}
      className="flex h-full flex-col"
    >
      <Screen
        header={<PersonFormHeader onBack={onBack} />}
        footer={
          <FooterWithProgressBar
            currentStepIndex={2}
            totalSteps={4}
            submitButtonText="Next"
            isLoading={methods.formState.isSubmitting}
            onSubmit={methods.handleSubmit(handleSubmit)}
          />
        }
      >
        <FormProvider {...methods}>
          <div className="flex flex-col gap-3 py-6">
            <Typography.Title size="s">Upload your ID</Typography.Title>
            <Typography.Text color="secondary">
              Take a&nbsp;clear&nbsp;photo of your&nbsp;ID. Make&nbsp;sure
              all&nbsp;details are&nbsp;readable and nothing is&nbsp;covered.
            </Typography.Text>
          </div>

          <div className="flex flex-col gap-3">
            <Controller
              name="type"
              control={methods.control}
              render={(fieldProps) => (
                <Select
                  {...fieldProps}
                  field={{
                    ...fieldProps.field,
                    onChange: (value: string | undefined) => {
                      fieldProps.field.onChange(value);
                      methods.resetField('document');
                      methods.resetField('idNumber');
                    },
                  }}
                  label="Type of ID"
                  labelPosition="inner"
                  renderValue={(id) => (id ? idDocumentTypeNames[id] : '')}
                >
                  {typeOptions.map(({ value, label }) => (
                    <Select.Option key={value} value={value}>
                      <Typography.Text>{label}</Typography.Text>
                      <Select.OptionIndicator />
                    </Select.Option>
                  ))}
                </Select>
              )}
            />

            {selectedType && (
              <Controller
                shouldUnregister
                name="document"
                control={methods.control}
                render={(fieldProps) => (
                  <FileUpload
                    ref={fileUploadRef}
                    {...fieldProps}
                    title={idDocumentUploadLabels[selectedType]}
                    options={{
                      allowedExtensions: ['png', 'jpg', 'jpeg'],
                      showProgress: false,
                    }}
                    onUpload={handleUpload}
                  />
                )}
              />
            )}

            {hasUploadedDocument && (
              <Controller
                shouldUnregister
                name="idNumber"
                control={methods.control}
                render={(fieldProps) => (
                  <InputMasked
                    {...fieldProps}
                    label={documentMetaMap[selectedType].label}
                    labelPosition="inner"
                    maskitoOptions={
                      documentMetaMap[selectedType].maskitoOptions
                    }
                    placeholder={documentMetaMap[selectedType].placeholder}
                  />
                )}
              />
            )}
          </div>
        </FormProvider>
      </Screen>
    </form>
  );
};
